<script lang="ts">
  import ActionsMenu, { type ActionItem } from './ActionsMenu.svelte';
  import BrandMark from './BrandMark.svelte';
  import ConfirmDialog from './ConfirmDialog.svelte';
  import DashboardTripCard from './DashboardTripCard.svelte';
  import Icon from './Icon.svelte';
  import InstallModal from './InstallModal.svelte';
  import RenameTripModal from './RenameTripModal.svelte';
  import ShareSheet from './ShareSheet.svelte';
  import {
    fetchUserDashboard,
    createTrip,
    deleteTrip,
    updateTrip,
    authSignOut,
    authDeleteAccount,
  } from './api';
  import { isStandalone } from './pwa';
  import type { DashboardTripView, UserDashboardView } from '~/shared/api.js';

  let { token }: { token: string } = $props();

  type FetchState =
    | { status: 'loading' }
    | { status: 'notFound' }
    | { status: 'error'; message: string }
    | { status: 'ready'; data: UserDashboardView };

  let fetchState: FetchState = $state({ status: 'loading' });
  let starting = $state(false);
  let signingOut = $state(false);
  let startError: string | null = $state(null);
  type DeleteState =
    | { kind: 'closed' }
    | { kind: 'confirming'; trip: DashboardTripView }
    | { kind: 'deleting'; trip: DashboardTripView };
  let deleteState: DeleteState = $state({ kind: 'closed' });
  let renameTrip: DashboardTripView | null = $state(null);
  let shareTrip: DashboardTripView | null = $state(null);
  let installOpen: boolean = $state(false);
  type DeleteAccountState =
    | { kind: 'closed' }
    | { kind: 'confirming'; error: string | null }
    | { kind: 'deleting' };
  let deleteAccount: DeleteAccountState = $state({ kind: 'closed' });
  const showInstall = !isStandalone();

  $effect(() => {
    fetchUserDashboard(token)
      .then((data) => {
        fetchState = data === null ? { status: 'notFound' } : { status: 'ready', data };
      })
      .catch((e: unknown) => {
        fetchState = {
          status: 'error',
          message: e instanceof Error ? e.message : 'Could not load your dashboard',
        };
      });
  });

  $effect(() => {
    if (fetchState.status === 'ready') {
      document.title = `Your trips • Lazy Traveler`;
    }
  });

  async function startTrip() {
    if (starting) return;
    starting = true;
    startError = null;
    try {
      const { token: tripToken } = await createTrip();
      window.location.assign(`/t/${tripToken}`);
    } catch (e) {
      startError = e instanceof Error ? e.message : 'Could not start a trip';
      starting = false;
    }
  }

  async function signOut() {
    if (signingOut) return;
    signingOut = true;
    try {
      await authSignOut();
    } finally {
      window.location.assign('/');
    }
  }

  async function confirmDeleteAccount() {
    if (deleteAccount.kind !== 'confirming') return;
    deleteAccount = { kind: 'deleting' };
    try {
      await authDeleteAccount();
      window.location.assign('/');
    } catch (e) {
      const error = e instanceof Error ? e.message : 'Could not delete account';
      deleteAccount = { kind: 'confirming', error };
    }
  }

  function requestDelete(trip: DashboardTripView) {
    deleteState = { kind: 'confirming', trip };
  }

  function requestRename(trip: DashboardTripView) {
    renameTrip = trip;
  }

  function requestShare(trip: DashboardTripView) {
    shareTrip = trip;
  }

  async function commitRename(next: string) {
    if (!renameTrip || fetchState.status !== 'ready') return;
    const target = renameTrip;
    const updated = await updateTrip(target.token, { name: next });
    fetchState = {
      status: 'ready',
      data: {
        ...fetchState.data,
        trips: fetchState.data.trips.map((t) =>
          t.token === target.token ? { ...t, name: updated.name } : t,
        ),
      },
    };
    renameTrip = null;
  }

  async function confirmDelete() {
    if (deleteState.kind !== 'confirming' || fetchState.status !== 'ready') return;
    const trip = deleteState.trip;
    deleteState = { kind: 'deleting', trip };
    try {
      await deleteTrip(trip.token);
      fetchState = {
        status: 'ready',
        data: {
          ...fetchState.data,
          trips: fetchState.data.trips.filter((t) => t.token !== trip.token),
        },
      };
      deleteState = { kind: 'closed' };
    } catch (e) {
      console.error('Failed to delete trip:', e);
      deleteState = { kind: 'confirming', trip };
    }
  }

  const accountMenuItems = $derived.by<ActionItem[]>(() => {
    if (fetchState.status !== 'ready') return [];
    const items: ActionItem[] = [];
    if (showInstall) {
      items.push({ kind: 'button', label: 'Install app', onClick: () => (installOpen = true) });
    }
    items.push({
      kind: 'button',
      label: signingOut ? 'Signing out…' : 'Sign out',
      disabled: signingOut,
      onClick: signOut,
    });
    items.push({
      kind: 'button',
      label: 'Delete account',
      destructive: true,
      onClick: () => (deleteAccount = { kind: 'confirming', error: null }),
    });
    return items;
  });
</script>

{#if fetchState.status === 'loading'}
  <div class="dash-state">
    <p class="dash-state-text">Loading…</p>
  </div>
{:else if fetchState.status === 'notFound'}
  <div class="dash-state">
    <h1 class="dash-state-title">Dashboard not found</h1>
    <p class="dash-state-text">This link doesn't match an account.</p>
    <a href="/" class="btn btn-tertiary">← back home</a>
  </div>
{:else if fetchState.status === 'error'}
  <div class="dash-state">
    <h1 class="dash-state-title">Couldn't load your dashboard</h1>
    <p class="dash-state-text">{fetchState.message}</p>
    <a href="/" class="btn btn-tertiary">← back home</a>
  </div>
{:else}
  <div class="dash-page">
    <header class="dash-topbar">
      <div class="dash-topbar-inner">
        <a href="/" class="brand" aria-label="Lazy Traveler home">
          <BrandMark size={28} />
          <span>Lazy Traveler</span>
        </a>
        <div class="account">
          <span class="account-email" title={fetchState.data.user.email}>
            {fetchState.data.user.email}
          </span>
          <ActionsMenu items={accountMenuItems} label="Account menu" size="md" emphasis="plain" />
        </div>
      </div>
    </header>

    <main class="dash-main">
      <section class="hero">
        <div class="hero-text">
          <h1 class="hero-title">Your trips</h1>
          <p class="hero-sub">
            {#if fetchState.data.trips.length === 0}
              Start one to save places you want to come back to.
            {:else if fetchState.data.trips.length === 1}
              1 trip saved
            {:else}
              {fetchState.data.trips.length} trips saved
            {/if}
          </p>
        </div>
        {#if fetchState.data.trips.length > 0}
          <button class="btn btn-primary hero-cta" type="button" onclick={startTrip} disabled={starting}>
            <Icon name="plus" size={16} />
            <span>{starting ? 'Starting…' : 'Start a trip'}</span>
          </button>
        {/if}
      </section>

      {#if startError}
        <p class="start-error">{startError}</p>
      {/if}

      {#if fetchState.data.trips.length === 0}
        <section class="empty">
          <div class="empty-icon" aria-hidden="true"><Icon name="sparkles" size={32} /></div>
          <h2 class="empty-title">No trips yet</h2>
          <p class="empty-text">
            Bookmark places, group them however you like, and decide later. Your trips show up here.
          </p>
          <button class="btn btn-primary" type="button" onclick={startTrip} disabled={starting}>
            {starting ? 'Starting…' : 'Start a trip'}
          </button>
        </section>
      {:else}
        <section class="trip-grid">
          {#each fetchState.data.trips as trip (trip.token)}
            <DashboardTripCard
              {trip}
              onShare={requestShare}
              onRename={requestRename}
              onDelete={requestDelete}
            />
          {/each}
        </section>
      {/if}
    </main>

    <footer class="dash-footer">
      <span class="footer-mark"><BrandMark size={20} /></span>
      <a href="/privacy">Privacy</a>
    </footer>
  </div>

  {#if deleteState.kind !== 'closed'}
    <ConfirmDialog
      title="Delete this trip?"
      message={`"${deleteState.trip.name}" will be permanently removed, along with all places, groups, and cities saved to it.`}
      confirmLabel={deleteState.kind === 'deleting' ? 'Deleting…' : 'Delete trip'}
      destructive
      onConfirm={confirmDelete}
      onCancel={() => (deleteState = { kind: 'closed' })}
    />
  {/if}

  {#if renameTrip}
    <RenameTripModal
      initialName={renameTrip.name}
      onSave={commitRename}
      onCancel={() => (renameTrip = null)}
    />
  {/if}

  {#if shareTrip}
    <ShareSheet token={shareTrip.token} onClose={() => (shareTrip = null)} />
  {/if}

  {#if installOpen}
    <InstallModal onClose={() => (installOpen = false)} />
  {/if}

  {#if deleteAccount.kind !== 'closed'}
    {@const error = deleteAccount.kind === 'confirming' ? deleteAccount.error : null}
    {@const n = fetchState.data.trips.length}
    {@const tripLine = n === 0
      ? 'No trips will be removed.'
      : `${n} ${n === 1 ? 'trip' : 'trips'} will be permanently deleted.`}
    <ConfirmDialog
      title="Delete your account?"
      message={error
        ? `${error} — try again?`
        : `${tripLine} Your email, sign-in history, and account will be erased. This can't be undone.`}
      confirmLabel={deleteAccount.kind === 'deleting' ? 'Deleting…' : 'Delete account'}
      destructive
      onConfirm={confirmDeleteAccount}
      onCancel={() => (deleteAccount = { kind: 'closed' })}
    />
  {/if}
{/if}

<style>
  .dash-page {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--color-canvas);
  }

  /* Top bar */
  .dash-topbar {
    position: sticky;
    top: 0;
    z-index: 10;
    background: var(--color-canvas);
    border-bottom: 1px solid var(--color-hairline-soft);
    backdrop-filter: saturate(160%) blur(8px);
  }
  .dash-topbar-inner {
    max-width: 1080px;
    margin: 0 auto;
    padding: 0.875rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  .brand {
    flex: 1;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: -0.01em;
    color: var(--color-ink);
    text-decoration: none;
  }
  .account {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }
  .account-email {
    font-size: 13px;
    color: var(--color-muted);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }

  /* Main content */
  .dash-main {
    flex: 1;
    width: 100%;
    max-width: 1080px;
    margin: 0 auto;
    padding: 2rem 1.25rem 3rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  /* Hero */
  .hero {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .hero-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
  .hero-title {
    margin: 0;
    font-size: 32px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--color-ink);
  }
  .hero-sub {
    margin: 0;
    font-size: 14px;
    color: var(--color-muted);
  }
  .hero-cta {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
  }

  .start-error {
    margin: 0;
    font-size: 13px;
    color: var(--color-error);
  }

  /* Empty state */
  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.75rem;
    padding: 3rem 1.5rem;
    border: 1px dashed var(--color-hairline);
    border-radius: var(--radius-md);
    background: var(--color-surface);
  }
  .empty-icon {
    color: var(--color-primary, var(--color-ink));
  }
  .empty-title {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: var(--color-ink);
  }
  .empty-text {
    margin: 0 auto;
    max-width: 420px;
    font-size: 14px;
    line-height: 1.55;
    color: var(--color-body);
  }

  /* Trip grid */
  .trip-grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: 1fr;
  }
  @media (min-width: 640px) {
    .trip-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (min-width: 1024px) {
    .trip-grid { grid-template-columns: repeat(3, 1fr); }
  }

  /* Footer */
  .dash-footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 1.5rem;
    font-size: 12px;
  }
  .footer-mark {
    color: var(--color-muted);
    display: inline-flex;
  }
  .dash-footer a {
    color: var(--color-muted);
    text-decoration: none;
  }

  /* Loading / error / 404 states */
  .dash-state {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 0.75rem;
    padding: 1.5rem;
    background: var(--color-canvas);
  }
  .dash-state-title {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--color-ink);
  }
  .dash-state-text {
    margin: 0;
    font-size: 14px;
    color: var(--color-muted);
  }
</style>
